
import React, { useState, useCallback, useRef } from 'react'

import { Box, Fade } from '@mui/material'

import { SnackbarContext } from './useSnackbar'
import { Alert, GENERIC_ERROR_TEXT, isTechnicalMessage, userFacingMessage } from '@repo/ui'
import { ROUTES } from 'routes/routes'
import { findDuplicateToast } from './toastDedupe'
import {
  apiFailureFromError,
  buildToastErrorContext,
  claimRecentApiFailure,
  errorSignatureKey,
  isApiError,
  type ApiFailure,
  type ToastErrorAttribution,
} from 'utils/errorContext'
import { writeErrorHandoff } from 'utils/supportHandoff'
import { reportShownError } from 'utils/errorReporter'


export interface NotificationProps {
  type: 'error' | 'success' | 'info' | 'warning' | undefined
  title?: string
  render?: () => React.ReactNode
  text?: string
  action?: React.ReactNode
  /**
   * Skip the auto-hide — stays open until the user clicks the X. For a message
   * that needs to be READ before something else happens (e.g. a heads-up right
   * before a native browser dialog can appear), not just a fire-and-forget status.
   */
  persist?: boolean
  /**
   * Override the auto-hide lifetime for THIS toast, in ms.
   *
   * The 5s default is deliberately locked to ScanSessionBlock's inline feedback (see
   * AUTO_HIDE_MS) and must not be changed globally to suit one message. But 5s is a lifetime
   * for a message you only READ, and too short for one carrying an ACTION: the reader has to
   * notice it, read it, decide, and travel to the button. Toasts with an action get longer.
   */
  durationMs?: number
  /**
   * 'bottom-right' (default) — routine confirmations, matches the standard
   * toast convention. 'top-center' — a message that needs to be seen the
   * instant it appears (sits in the natural top-down scan path); use sparingly,
   * for things like the reader-permission heads-up ahead of a native dialog.
   */
  position?: 'bottom-right' | 'top-center' | 'bottom-center'
  /**
   * VRG-721 — the error this toast is about, when the caller has it.
   *
   * Optional, because most call sites only pass `getErrorMessage(error)` and the
   * provider finds the failure itself (the API client records every failed
   * request; see `utils/errorContext`). Passing it is still better: it is the
   * one attribution that is not a heuristic. Never rendered — it feeds the
   * "Report a problem" handoff and the quiet automatic report only.
   */
  error?: unknown
}

/**
 * Routes whose toasts default to `bottom-center` instead of `bottom-right`.
 *
 * The auth screens are a single narrow column centred in an otherwise empty
 * viewport, so a bottom-RIGHT toast lands in dead space diagonally opposite the
 * thing the user is looking at — on a wide monitor, far enough away to be missed
 * entirely. Every message on these screens is about the form ("Verification email
 * sent", "Incorrect password"), so it belongs under the form.
 *
 * Matched on the pathname rather than passed per call site, because a `position`
 * prop that each caller must remember is the opt-in 35-of-37 call sites forget
 * (working-conventions §42). An explicit `position` still wins.
 */
const BOTTOM_CENTER_ROUTES = [
  '/sign-in',
  '/sign-up',
  '/signup',
  '/verify-email',
  '/reset-password',
]

/**
 * Read at toast-CREATION time, deliberately.
 *
 * `SnackbarProvider` wraps `BrowserRouter` (see `index.tsx`), so it cannot call
 * `useLocation` — and does not need to. A toast's position is decided once, when
 * it opens, and never changes for its lifetime, so a plain `window.location` read
 * is both sufficient and immune to the provider sitting outside the router.
 */
const defaultPositionForRoute = (): NotificationProps['position'] => {
  const path = window.location.pathname
  return BOTTOM_CENTER_ROUTES.some((r) => path.startsWith(r))
    ? 'bottom-center'
    : 'bottom-right'
}

interface Props {
  children: React.ReactNode
}

interface ToastItem extends NotificationProps {
  id: number
  // Set when a dismissal (X click, auto-hide, or close()) has been requested — flips
  // the Fade to `in={false}` so it plays the exit transition. The item stays in `toasts`
  // (and keeps its layout slot, so siblings don't jump) until onExited actually removes it.
  closing?: boolean
  // VRG-719 — recorded at creation for `findDuplicateToast`: a caller-supplied action or
  // render makes two same-text toasts genuinely different (two Undo's for two records).
  hasOwnAction?: boolean
  hasRender?: boolean
}

let toastIdSeq = 0

/**
 * How long a non-persistent toast stays up.
 *
 * 9s, matching `FEEDBACK_AUTO_CLEAR_MS` in ScanSessionBlock — the inline scan feedback line.
 * The two surfaces routinely report the SAME event (a refused scan raises the inline line and
 * a toast), so a different lifetime meant the two visibly disagreed about how long the error
 * was worth reading: the line cleared itself while its toast sat there for another three
 * seconds. Whichever number is right, it has to be one number. If you change it here, change
 * it there (and mind that side's MIN_VISIBLE guard, which is a floor, not a lifetime).
 *
 * VRG-607 — raised 5s -> 9s. 5s is enough to notice a toast but not to READ one and decide,
 * and toasts are now the surface carrying a "Report a problem" link, which needs travel time.
 * Both numbers moved together, because the sentence above is the whole reason this constant
 * is documented at all: the scan line and its toast must not disagree.
 *
 * KNOWN DIVERGENCE, called out rather than hidden: the scan line's 5s was "per the brief" and
 * sat 1s under the phone app's own 6s expiry of the same message. At 9s the browser now sits
 * 3s ABOVE the phone. That is a deliberate trade — one consistent browser lifetime beat
 * matching a different runtime — but if the phone is the surface that matters for scanning,
 * this is the line to revisit, not the global default.
 */
const AUTO_HIDE_MS = 9000

/**
 * VRG-607 — lifetime for an error toast carrying the auto-attached
 * "Report a problem" link. Longer than AUTO_HIDE_MS for the reason
 * NotificationProps.durationMs already documents: a toast you only read needs
 * 5s, a toast you might ACT on needs time to notice, read, decide and travel.
 *
 * Not applied to the 5s ScanSessionBlock inline feedback that AUTO_HIDE_MS is
 * deliberately locked to — that path passes its own durationMs, which still wins.
 */
const ERROR_WITH_ACTION_MS = 9000

// One toast row — its own fade in/out and its own auto-hide timer, independent of every
// other toast in the stack. `onRequestClose` starts the exit transition; `onExited` (fired
// by Fade once that transition finishes) is what actually removes it from state.
const ToastRow = ({
  toast,
  onRequestClose,
  onExited,
}: {
  toast: ToastItem
  onRequestClose: () => void
  onExited: () => void
}) => (
  /* VRG-607 — was `timeout={200}` with opacity only, which read as a flash rather
     than as something arriving. Two reasons it did:

     1. NOTHING MOVED. A pure cross-fade gives the eye no direction, so the toast
        appears to blink into place instead of entering from somewhere. It now
        travels 24px in from the right edge it is anchored to, so the motion
        explains where it came from.
     2. 200ms is below the threshold where an ease is legible — the curve had no
        room to read, making even the fade feel instantaneous.

     Enter 280ms / exit 180ms: asymmetric on purpose. Arriving wants to be noticed,
     leaving should not hold attention, and a slow exit makes a dismissed toast feel
     like it is resisting the click.

     cubic-bezier(0.32, 0.72, 0, 1) is the curve apps/app already uses for its page
     transitions — decelerating hard at the end, so the toast settles rather than
     stopping dead. Borrowed rather than invented so the two apps move alike.

     `Fade` is kept rather than swapped for `Slide` because the removal lifecycle
     depends on its `onExited` firing (see removeToast); the translate rides on the
     child, which MUI leaves alone. */
  <Fade in={!toast.closing} appear timeout={{ enter: 280, exit: 180 }} onExited={onExited}>
    {/* Fade's DIRECT child is left alone: MUI writes an inline `style` with its own
        opacity transition onto it, and an inline style beats `sx`. A transform
        transition declared here was silently dropped — measured in the browser,
        computed `transition-property` came back as `opacity` alone and
        `getAnimations()` was empty, so the toast only ever cross-faded. That is
        what read as jumpy: no travel, just a blink.
        The movement therefore lives on an inner element, where nothing overwrites
        it. Opacity is Fade's; transform is ours; neither touches the other. */}
    <Box>
      <Box
        sx={{
          willChange: 'transform',
          '@keyframes veraToastIn': {
            from: { transform: 'translateX(24px)' },
            to: { transform: 'translateX(0)' },
          },
          // Exit is a transition, enter is a keyframe: on exit the element already
          // exists, so a transition is enough; on enter it must start offset before
          // its first paint, which only a keyframe can express.
          transition: 'transform 180ms cubic-bezier(0.32, 0.72, 0, 1)',
          transform: toast.closing ? 'translateX(24px)' : 'translateX(0)',
          ...(toast.closing
            ? {}
            : {
                // `both` holds the final frame; without a fill-mode the element
                // snaps back to its pre-animation transform for one frame as the
                // animation ends — a hitch at exactly the moment it should settle.
                animation: 'veraToastIn 280ms cubic-bezier(0.32, 0.72, 0, 1) both',
              }),
        }}
      >
      <Alert
        severity={toast.type}
        variant="outlined"
        neutral
        title={toast.title}
        description={toast.text}
        button={toast.action}
        onClose={onRequestClose}
        // Toast copy reads as normal body text (not the Alert's semibold title).
        // A touch bigger + a min width so short toasts still have presence; a max width
        // + word-break so a long message wraps inside the box instead of overflowing.
        sx={{
          // VRG-607 — a FIXED width, not a floor.
          //
          // `minWidth` alone produced a ragged column: an error toast carries the
          // "Report a problem" link, which pushed it past the floor while a short
          // success toast sat exactly on it. Stacked, three different widths read as
          // an accident rather than a decision.
          //
          // 440 is sized to the widest routine case — title + link on one line —
          // so nothing needs to exceed it and every toast in the column aligns.
          //
          // This also answers what looked like "too much right padding" on a short
          // toast: it was never padding (the Alert's is a symmetric 12px/16px, now
          // 12px/12px), it was the old 480 floor stranding the close X far past the
          // end of a 280px sentence.
          width: 440,
          maxWidth: 'min(600px, 92vw)',
          // VRG-607 — `px: 4` was 32px on BOTH sides and is what actually produced
          // the roomy right edge. It overrides the shared Alert's own padding
          // entirely, so adjusting `@repo/ui` had no effect here at all — measured
          // in the browser: computed paddingLeft/Right were 32px while the component
          // declared 16px.
          //
          // Now 16px left (the icon wants the inset) and 12px right: the close X is
          // an icon button carrying its own hit area, so a further 16 beyond it read
          // as a gap rather than as margin.
          py: 2.5,
          pl: '16px',
          pr: '12px',
          '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' },
          '& .MuiAlertTitle-root': { fontWeight: 400, fontSize: 15, overflowWrap: 'anywhere' },
          '& .MuiTypography-body3': { fontSize: 13, overflowWrap: 'anywhere' },
        }}
      >
        {toast.render?.()}
      </Alert>
      </Box>
    </Box>
  </Fade>
)

export const SnackbarProvider = ({ children }: Props) => {
  const [toasts, setToastState] = useState<ToastItem[]>([])
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  // VRG-719 — the stack is mirrored in a ref, updated SYNCHRONOUSLY with every change,
  // so `openSnackbar` can ask "is this toast already open?" at call time. Reading the
  // `toasts` state there would see the stack as of the last render: two failures landing
  // in the same tick would both miss each other and stack anyway.
  const toastsRef = useRef<ToastItem[]>([])
  const setToasts = useCallback((update: (prev: ToastItem[]) => ToastItem[]) => {
    toastsRef.current = update(toastsRef.current)
    setToastState(toastsRef.current)
  }, [])

  // Actually drops the toast from state — only called once its exit transition
  // (Fade's onExited) has finished playing.
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [setToasts])

  // Starts a toast's exit transition (flips `closing`); removeToast finishes the job
  // once Fade reports the animation is done. Used by the X button, auto-hide, and close().
  const requestClose = useCallback((id: number) => {
    setToasts(prev => prev.map(t => (t.id === id ? { ...t, closing: true } : t)))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [setToasts])

  /**
   * VRG-607 — the single place every toast in this app is created, and therefore
   * the only place raw engineering text can be stopped once for all of them.
   *
   * An audit found 164 call sites feeding error text into `title`/`text`, in four
   * shapes: `getErrorMessage(error)` passed directly (69), `error.message` passed
   * directly (40), the same hoisted into a local first (49), and the error
   * interpolated mid-sentence (2, which read worst — "Unable to upload products:
   * Request failed with status code 400. Please check your file format").
   *
   * Roughly half have NO fallback copy at all, so whatever the error string says
   * IS the whole toast. The rest use `error.message || 'Something friendly'`,
   * where the friendly half is unreachable: `.message` is always populated on an
   * axios error, so `||` never fires. Copy behind a `||` that cannot run reads as
   * handled and isn't — that is the shape worth recognising.
   *
   * Fixing 164 call sites would fix them once and hold until the 165th.
   * Filtering here holds by construction (working-conventions §42).
   *
   * `getErrorMessage` (utils/parsers.ts) is fixed separately as the second line of
   * defence — it is the source of ~120 of these, and it already carries a comment
   * naming this exact axios string before falling through to it anyway.
   *
   * Severity is untouched: only the words change, never whether it reads as an error.
   */
  const openSnackbar = useCallback((props: NotificationProps) => {
    const isError = props.type === 'error'

    // A technical TITLE is replaced outright rather than kept above a generic
    // body it would not match.
    const safeTitle =
      props.title && isTechnicalMessage(props.title)
        ? isError
          ? 'Something went wrong'
          : undefined
        : props.title

    // A toast with no body keeps none — a generic sentence it never asked for is
    // noise, and this app's toast policy already prefers inline errors for detail.
    const sanitisedText = props.text
      ? userFacingMessage(props.text, isError ? GENERIC_ERROR_TEXT : '')
      : props.text

    // VRG-607 — drop the body when it now says the same thing as the title.
    //
    // The two substitutions above are independent: a technical TITLE becomes
    // "Something went wrong", and a technical BODY falls back to a generic
    // sentence. Neither knows about the other, so a toast whose title AND body
    // were both unusable ends up saying it twice:
    //
    //     Something went wrong
    //     Something went wrong.
    //
    // Observed in the components lab, which passes `title: 'Error'`. Real call
    // sites usually carry a real title ("Payment failed") and so avoid it, but
    // "usually" is not a guarantee and the repetition reads like a bug.
    //
    // Compared with punctuation and case stripped, because the two strings differ
    // only by a trailing full stop — which is exactly the difference a reader does
    // not register.
    const flatten = (s?: string) => s?.trim().replace(/[.!?]+$/, '').toLowerCase()
    const safeText =
      flatten(sanitisedText) && flatten(sanitisedText) === flatten(safeTitle)
        ? undefined
        : sanitisedText

    /*
      VRG-721 — WHICH failure is this toast about?

      Claimed BEFORE the duplicate check below, on purpose: a duplicate toast is not
      shown, but the failure it was raised for has still been accounted for, and
      leaving it unclaimed would let the NEXT, unrelated toast inherit it.
    */
    let failure: ApiFailure | null = null
    let attribution: ToastErrorAttribution | null = null
    if (isError) {
      if (props.error !== undefined && isApiError(props.error)) {
        failure = apiFailureFromError(props.error)
        attribution = failure ? 'explicit' : null
      } else if (props.error === undefined) {
        const claimed = claimRecentApiFailure({ title: props.title, text: props.text })
        failure = claimed?.failure ?? null
        attribution = claimed?.attribution ?? null
      }
    }

    // Route-derived unless the caller was explicit — see `defaultPositionForRoute`.
    // Resolved HERE, once, so the toast keeps the position it opened with even if the
    // user navigates away mid-toast.
    const position = props.position ?? defaultPositionForRoute()

    // VRG-719 — the same error, already on screen, is not shown twice. See
    // `toastDedupe.ts` for the prod incident (four stacked "Something went wrong"
    // toasts from four Create clicks) and for why the rule is errors-only and skips
    // toasts that carry their own action.
    if (
      findDuplicateToast(toastsRef.current, {
        type: props.type,
        title: safeTitle,
        text: safeText || undefined,
        position,
        hasOwnAction: Boolean(props.action),
        hasRender: Boolean(props.render),
      })
    ) {
      return
    }

    const id = ++toastIdSeq

    /*
      VRG-721 — the record of what the user just saw, built from the FINAL title and
      text (what is on screen, after sanitising) plus the facts of the failure. It is
      a whitelist: see `utils/errorContext` for what is and is never in it.

      Two consumers, and both are why "Report a problem" used to be useless:
        1. the handoff — written now, keyed by an id the link carries, so the support
           form opens as a bug report that already says what failed;
        2. the quiet automatic report — filed now, whether or not anyone clicks,
           because most people who hit an error do not write in.
    */
    let handoffId: string | null = null
    if (isError) {
      try {
        const errorContext = buildToastErrorContext({
          title: safeTitle,
          text: safeText || undefined,
          failure,
          attribution,
          page: window.location.pathname,
        })
        const signatureKey = errorSignatureKey(errorContext)
        handoffId = writeErrorHandoff(errorContext, signatureKey)
        const messageWasTechnical =
          isTechnicalMessage(props.title) ||
          isTechnicalMessage(props.text) ||
          (failure?.kind === 'api' && (!failure.serverMessage || isTechnicalMessage(failure.serverMessage)))
        reportShownError(errorContext, {
          hasFieldErrors: failure?.hasFieldErrors,
          messageWasTechnical,
          explicitClientError: props.error instanceof Error && !isApiError(props.error),
        })
      } catch {
        /* the toast must open even if every line above failed */
      }
    }
    const reportHref = `${ROUTES.SUPPORT_NEW}?type=bug${handoffId ? `&error=${encodeURIComponent(handoffId)}` : ''}`

    /**
     * VRG-607 — an error toast offers a way to report it.
     *
     * A plain <a>, not a react-router <Link>: this provider WRAPS BrowserRouter
     * (see index.tsx) rather than sitting inside it, so router hooks are not
     * available here. The resulting full page load is acceptable — arguably
     * right — when the user is leaving to report that something broke.
     *
     * Only when the caller supplied no `action` of its own: a toast that already
     * offers "Undo" or "View invoice" has a better next step than we do, and
     * replacing it would be a downgrade.
     */
    const action =
      isError && !props.action ? (
        <a
          href={reportHref}
          onClick={e => {
            // VRG-607 — open the intake modal OVER the current page instead of
            // hard-navigating to it.
            //
            // `/support/new` is a route-BACKED modal: SupportIntakeProvider derives
            // `isOpen` from `useMatch(ROUTES.SUPPORT_NEW)` and keeps the previous
            // location in `backgroundRef` so the modal floats above whatever you
            // were doing. A plain <a> defeats that entirely — the browser tears the
            // app down and rebuilds it at the URL, so there IS no background page
            // and you watch a route change before the modal appears. Worse, on an
            // error toast that is precisely the moment not to throw away the screen
            // the user was trying to report.
            //
            // pushState + popstate rather than `useNavigate`: this provider WRAPS
            // BrowserRouter (see index.tsx), so router hooks are unavailable here.
            // React Router listens for popstate, so this is the supported way to
            // hand it a navigation from outside its tree.
            //
            // Modified clicks (cmd/ctrl/shift/middle) fall through to the browser,
            // so "open in new tab" still behaves like a link.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
            e.preventDefault()
            // VRG-721 — `?type=bug&error=<handoff id>`: the form opens as a bug report
            // carrying this toast's error. It used to push the bare route, so the form
            // defaulted to "Contact support" and knew nothing about what had failed.
            window.history.pushState({}, '', reportHref)
            window.dispatchEvent(new PopStateEvent('popstate'))
            // VRG-721 — the toast has handed its error to the form, so its job is done.
            // Left up, it is a persistent error toast pinned bottom-right, which is
            // exactly where the intake modal's Submit button sits at 1280x800: the
            // preview walkthrough showed it covering the button the user needs next.
            requestClose(id)
          }}
          style={{
            color: 'inherit',
            fontSize: 13,
            fontWeight: 500,
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            whiteSpace: 'nowrap',
          }}
        >
          Report a problem
        </a>
      ) : (
        props.action
      )

    setToasts(prev => [
      ...prev,
      {
        ...props,
        // Not kept on the toast: it was only needed to build the handoff above, and
        // an error object in state would pin its request config in memory.
        error: undefined,
        title: safeTitle,
        text: safeText || undefined,
        action,
        id,
        position,
        hasOwnAction: Boolean(props.action),
        hasRender: Boolean(props.render),
      },
    ])
    // VRG-607 — an ERROR toast stays until the user dismisses it.
    //
    // Errors are the one class where missing the message has a cost. A success
    // toast can expire safely because the state visibly changed and the toast was
    // only confirming it; an error that auto-hides leaves someone with a failed
    // action, no explanation, and no way to recover the sentence. They now also
    // carry a "Report a problem" link, and an affordance that removes itself on a
    // timer is not one.
    //
    // A caller can still opt out with `persist: false`, which is why the check is
    // `!== false` rather than a plain default.
    //
    // ⚠️ ScanSessionBlock is the case that probably NEEDS that opt-out and does not
    // yet pass it: it raises 9 error toasts at scanner frequency, each paired with
    // an inline line that self-clears, so undismissed toasts would stack up behind
    // an operator working quickly. Not changed here because it is 9 call sites and
    // wants its own look; flagged rather than silently left as a surprise.
    const shouldPersist = isError ? props.persist !== false : props.persist
    if (!shouldPersist) {
      // VRG-607 — an error toast now carries a "Report a problem" link, and this
      // file's own NotificationProps docblock already states the reason this
      // matters: 5s is a lifetime for a message you only READ, and too short for
      // one carrying an ACTION, because the reader has to notice it, read it,
      // decide, and travel to the link. Adding the affordance without the time to
      // use it would be an affordance in name only.
      //
      // An explicit `durationMs` from the caller still wins.
      const lifetime =
        props.durationMs ?? (isError && !props.action ? ERROR_WITH_ACTION_MS : AUTO_HIDE_MS)
      const timer = setTimeout(() => requestClose(id), lifetime)
      timers.current.set(id, timer)
    }
  }, [requestClose, setToasts])

  // No id passed in from callers (the original single-toast API didn't have
  // one) — closing "the" toast now means the most recently opened one, the
  // closest equivalent to the old single-toast behavior.
  const closeSnackbar = useCallback(() => {
    setToasts(prev => {
      const last = prev[prev.length - 1]
      return last ? prev.map(t => (t.id === last.id ? { ...t, closing: true } : t)) : prev
    })
  }, [setToasts])

  // `bottom-right` is the fallback for anything unrecognised, so a toast can
  // never end up in no stack at all and silently fail to render.
  const bottomToasts = toasts.filter(
    t => t.position !== 'top-center' && t.position !== 'bottom-center',
  )
  const topToasts = toasts.filter(t => t.position === 'top-center')
  const bottomCenterToasts = toasts.filter(t => t.position === 'bottom-center')

  return (
    <SnackbarContext.Provider
      value={{
        open: openSnackbar,
        close: closeSnackbar,
      }}
    >
      {children}

      {/* Bottom-right stack — newest toast appended last renders as the LAST
          flex child, i.e. closest to the fixed bottom edge (the anchor); older
          ones get pushed upward as more arrive. */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 40,
          right: 40,
          zIndex: (theme) => theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'flex-end',
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        {bottomToasts.map((t) => (
          <ToastRow
            key={t.id}
            toast={t}
            onRequestClose={() => requestClose(t.id)}
            onExited={() => removeToast(t.id)}
          />
        ))}
      </Box>

      {/* Bottom-center stack — the auth screens (see `BOTTOM_CENTER_ROUTES`).
          Same bottom anchor and same `column` order as the bottom-right stack,
          so the newest sits closest to the edge and older ones ride upward; only
          the horizontal anchor differs, which keeps the two reading as one
          convention rather than two. */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: (theme) => theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        {bottomCenterToasts.map((t) => (
          <ToastRow
            key={t.id}
            toast={t}
            onRequestClose={() => requestClose(t.id)}
            onExited={() => removeToast(t.id)}
          />
        ))}
      </Box>

      {/* Top-center stack — 'column-reverse' so the newest (last-appended)
          renders as the FIRST visual row, closest to the fixed top edge. */}
      <Box
        sx={{
          position: 'fixed',
          top: 32,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: (theme) => theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: 2,
          alignItems: 'center',
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        {topToasts.map((t) => (
          <ToastRow
            key={t.id}
            toast={t}
            onRequestClose={() => requestClose(t.id)}
            onExited={() => removeToast(t.id)}
          />
        ))}
      </Box>
    </SnackbarContext.Provider>
  )
}
